import {createContext, useContext, useState, useEffect, useCallback, ReactNode} from 'react'
import axios from 'axios'

interface UserInfo {
    userId: number
    firstName: string
    lastName: string
    email: string
    userName: string
    isAdminUser: boolean
}

interface AuthContextType {
    isAuthenticated: boolean
    isLoading: boolean
    user: UserInfo | null
    isAdminUser: boolean
    authUrl: string | null
    checkAuth: () => Promise<void>
    logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({children}: {children: ReactNode}) {
    const [isAuthenticated, setIsAuthenticated] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [user, setUser] = useState<UserInfo | null>(null)
    const [authUrl, setAuthUrl] = useState<string | null>(null)

    const fetchAuthUrl = useCallback(async () => {
        try {
            const {data} = await axios.get('/api/config/auth-url')
            setAuthUrl(data.authUrl)
        } catch (e) {
            console.error('[Auth] Failed to fetch auth URL:', e)
        }
    }, [])

    const fetchUser = useCallback(async () => {
        try {
            const {data} = await axios.get<UserInfo>('/api/admin/user')
            setUser(data)
        } catch (e) {
            console.error('[Auth] Failed to fetch user info:', e)
        }
    }, [])

    const checkAuth = useCallback(async () => {
        try {
            await axios.get('/api/auth/check')
            setIsAuthenticated(true)
            await fetchUser()
        } catch (e) {
            setIsAuthenticated(false)
        }
    }, [fetchUser])

    const logout = useCallback(async () => {
        try {
            await axios.post('/api/auth/logout')
            setIsAuthenticated(false)
            setUser(null)
        } catch (e) {
            console.error('Failed to logout:', e)
        }
    }, [])

    const handleTokenFromUrl = useCallback(async () => {
        const urlParams = new URLSearchParams(window.location.search)
        // URLSearchParams decodes '+' as space, so we need to restore them
        const rawToken = urlParams.get('token')
        const token = rawToken ? rawToken.replace(/ /g, '+') : null

        console.log('[Auth] handleTokenFromUrl called, token exists:', !!token)

        if (token) {
            try {
                console.log('[Auth] Sending token to server...')
                const response = await axios.post('/api/auth/token', { token })
                console.log('[Auth] Token response:', response.data)
                setIsAuthenticated(true)
                await fetchUser()
                
                // Remove token from URL
                urlParams.delete('token')
                const cleanUrl = window.location.pathname + 
                    (urlParams.toString() ? '?' + urlParams.toString() : '')
                window.history.replaceState({}, document.title, cleanUrl)
            } catch (e: any) {
                console.error('[Auth] Failed to set auth token:', e.response?.data || e.message)
                setIsAuthenticated(false)
            }
        } else {
            console.log('[Auth] No token in URL, checking existing auth...')
            await checkAuth()
        }
    }, [checkAuth])

    useEffect(() => {
        const init = async () => {
            setIsLoading(true)
            await Promise.all([handleTokenFromUrl(), fetchAuthUrl()])
            setIsLoading(false)
        }
        init()
    }, [handleTokenFromUrl, fetchAuthUrl])

    return (
        <AuthContext.Provider value={{
            isAuthenticated,
            isLoading,
            user,
            isAdminUser: user?.isAdminUser ?? false,
            authUrl,
            checkAuth,
            logout,
        }}>
            {children}
        </AuthContext.Provider>
    )
}

export function useAuth() {
    const context = useContext(AuthContext)
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}
